import { useEffect, useRef, useState } from "react";
import type { PdfCanvasMarkHoverState, PdfCanvasStroke } from "../pdfCanvasTypes";

/** İşaret üzerinde bu kadar durunca panel açılır (geçerken titremesin). */
const MARK_HOVER_OPEN_DELAY_MS = 150;
/** İşaretten / panelden çıkınca bu kadar sonra kapanır (işaretten panele geçiş payı). */
const MARK_HOVER_CLOSE_DELAY_MS = 300;

/**
 * Renk panelinin tooltip mantığı: işaret üzerinde durunca açılır, mouse işaretten ve panelden
 * uzaklaşınca kendiliğinden kapanır. Konum açılış anındaki mouse noktasında sabit kalır.
 */
export function usePdfMarkHover() {
    const [hoveredMark, setHoveredMark] = useState<PdfCanvasMarkHoverState | null>(null);

    const hoveredMarkRef = useRef<PdfCanvasMarkHoverState | null>(null);
    const pendingMarkRef = useRef<PdfCanvasMarkHoverState | null>(null);
    const openTimerRef = useRef<number | null>(null);
    const closeTimerRef = useRef<number | null>(null);
    const isPopoverHoveredRef = useRef(false);
    // Yeni çizilen / az önce rengi değişen işaret, mouse üzerinden bir kez ayrılana kadar panel açmaz.
    const suppressedStrokeRef = useRef<PdfCanvasStroke | null>(null);

    const clearOpenTimer = () => {
        if (openTimerRef.current !== null) {
            window.clearTimeout(openTimerRef.current);
            openTimerRef.current = null;
        }
        pendingMarkRef.current = null;
    };

    const clearCloseTimer = () => {
        if (closeTimerRef.current !== null) {
            window.clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    };

    const show = (mark: PdfCanvasMarkHoverState | null) => {
        hoveredMarkRef.current = mark;
        setHoveredMark(mark);
    };

    const hideNow = () => {
        clearOpenTimer();
        clearCloseTimer();
        isPopoverHoveredRef.current = false;
        if (hoveredMarkRef.current) {
            show(null);
        }
    };

    const scheduleClose = () => {
        clearOpenTimer();
        if (!hoveredMarkRef.current || isPopoverHoveredRef.current || closeTimerRef.current !== null) {
            return;
        }

        closeTimerRef.current = window.setTimeout(() => {
            closeTimerRef.current = null;
            if (!isPopoverHoveredRef.current) {
                show(null);
            }
        }, MARK_HOVER_CLOSE_DELAY_MS);
    };

    const updateCandidate = (candidate: PdfCanvasMarkHoverState | null) => {
        if (candidate && candidate.stroke === suppressedStrokeRef.current) {
            candidate = null;
        }
        else {
            suppressedStrokeRef.current = null;
        }

        if (!candidate) {
            scheduleClose();
            return;
        }

        if (hoveredMarkRef.current?.stroke === candidate.stroke) {
            clearOpenTimer();
            clearCloseTimer();
            return;
        }

        if (pendingMarkRef.current?.stroke === candidate.stroke) {
            return;
        }

        clearOpenTimer();
        pendingMarkRef.current = candidate;
        openTimerRef.current = window.setTimeout(() => {
            openTimerRef.current = null;
            const pending = pendingMarkRef.current;
            pendingMarkRef.current = null;
            if (pending) {
                clearCloseTimer();
                show(pending);
            }
        }, MARK_HOVER_OPEN_DELAY_MS);
    };

    const suppressStroke = (stroke: PdfCanvasStroke) => {
        suppressedStrokeRef.current = stroke;
    };

    const onPopoverEnter = () => {
        isPopoverHoveredRef.current = true;
        clearCloseTimer();
    };

    const onPopoverLeave = () => {
        isPopoverHoveredRef.current = false;
        scheduleClose();
    };

    // Panel açılış anındaki viewport noktasına sabit; içerik kayarsa işaretten kopmasın diye kapat.
    useEffect(() => {
        if (!hoveredMark) {
            return;
        }

        const onScroll = () => hideNow();
        document.addEventListener("scroll", onScroll, { capture: true, passive: true });
        return () => document.removeEventListener("scroll", onScroll, { capture: true });
    }, [hoveredMark]);

    useEffect(() => () => {
        clearOpenTimer();
        clearCloseTimer();
    }, []);

    return {
        hoveredMark,
        updateCandidate,
        scheduleClose,
        hideNow,
        suppressStroke,
        onPopoverEnter,
        onPopoverLeave,
    };
}
